import { useContext, useState } from "react";
import styles from "./styles.module.scss";
import { ComplexContractInputs } from "../create";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import ConfirmationModal from "../create/confirmationModal";
import {
  ERROR_PATTERNS,
  ESenderRole,
  EPaymentBrand,
  ETrasactionsType,
  IContractDetailsResponseData,
  PUBLISH_ERROR_CODES,
  Transactions,
} from "../../../setup/api/services/contract/models";
import { ECONTRACT_STATUS } from "../../../enums";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { ROUTES } from "../../../setup/constants";
import { GET_MILESTONE_STATUSES } from "../../../components/contract/milestones/constants";
import BreadCrumbs from "../breadCrumbs";
import { ReactComponent as DeleteResource } from "../../../assets/images/svg/deleteResource.svg";
import { ReactComponent as LeftArrow } from "../../../assets/svg/leftArrow.svg";
import { ContractContext } from "../../../providers/contract";
import PrimaryButton from "../../../components/common/PrimaryButton";
import { publishExternalDraftContract } from "../../../setup/api/services/contract";
import Spinner from "../../../components/common/Spinner";
import PublishResultModal from "./publishResultModal";
import { getArabicOrdinalFemale } from "../../../setup/util/arabicOrdinalName";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import SettlementRequests from "./SettlementRequests";

export type ExtendedContract = IContractDetailsResponseData & {
  title: string;
  deadline: string;
};

interface IComplexContractDetailsProps {
  title: string;
  description: string;
  milestonesArray: ComplexContractInputs["milestones"] | ExtendedContract[];
  handleAddMilestone?: () => void;
  handleDeleteMilestone?: (milestoneIndex: number) => void;
  handleEditMilestone?: (milestoneIndex: number) => void;
  totalAmount?: number;
  totalParties?: number;
  totalMilestones?: number;
  showBreadCrumbs?: boolean;
}

const ComplexContractDetails = ({
  milestonesArray,
  title,
  description,
  handleAddMilestone,
  handleDeleteMilestone,
  handleEditMilestone,
  totalAmount,
  totalParties,
  totalMilestones,
  showBreadCrumbs,
}: IComplexContractDetailsProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const authorities = AuthConfig.getUserInfo()?.authorities;
  const { id: complexId } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(false);
  const [publishResultModalOpen, setPublishResultModalOpen] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [publishedMilestones, setPublishedMilestones] = useState<
    { name: string; index: number }[]
  >([]);
  const [publishErrors, setPublishErrors] = useState<
    Record<string, { index: number; errors: string[] }>
  >({});
  const { actions, contract } = useContext(ContractContext);

  type PartyLike = {
    type?: string;
    amount?: number;
    fee?: number;
    phoneNumber?: string;
  };

  type MilestoneLike = {
    id?: string;
    title?: string;
    paymentMethods?: {
      cards: string[];
      apple_pay: string[];
      tabby: string[];
      bank_transfer: string[];
    };
    images?: string[];
    deadline?: string;
    status?: string;
    milestoneReleaseTerms?: string;
    milestoneCancelTerms?: string;
    parties?: PartyLike[];
    parentId?: string;
    transactions?: Transactions[];
  };

  const milestones = (milestonesArray as unknown as MilestoneLike[]) || [];

  const totalAmountForComplex = milestones.reduce((acc, milestone) => {
    const currentMilestoneParties = milestone?.parties;
    const currentCustomersAmount = currentMilestoneParties
      ?.filter((party) => (party as { type: string })?.type === "customer")
      ?.reduce((innerAcc, cur) => {
        return +innerAcc + +(cur?.amount || 0);
      }, 0);
    const currentProvidersAmount = currentMilestoneParties
      ?.filter((party) => (party as { type: string })?.type === "provider")
      ?.reduce((innerAcc, cur) => {
        return +innerAcc + +(cur?.amount || 0);
      }, 0);
    return (
      +acc + Math.max(currentCustomersAmount || 0, currentProvidersAmount || 0)
    );
  }, 0);

  const uniquePhoneCount = new Set(
    milestones
      .flatMap((m) => m?.parties || [])
      .map((p) => p?.phoneNumber)
      .filter(Boolean)
  ).size;

  const complexStats = {
    totalAmountForComplex: (
      totalAmount || totalAmountForComplex
    )?.toLocaleString(),
    totalPartiesForComplex: totalParties ?? uniquePhoneCount,
    totalNumberOfMilestonesForComplex:
      totalMilestones || milestones?.length || 0,
  };

  const { seller } = GET_MILESTONE_STATUSES(t);

  const isCreateMatch = matchPath(
    { path: ROUTES.CREATE_COMPLEX_CONTRACT },
    location.pathname
  );

  function extractErrorVariables(code: string, message: string): string[] {
    const pattern = ERROR_PATTERNS[code];
    if (!pattern) return [];

    const match = message.match(pattern);
    return match ? match.slice(1) : [];
  }

  function formatMilestoneValidationErrors(
    validationErrors: Record<string, any>
  ) {
    // Group errors by milestone with index info
    const groupedErrors: Record<string, { index: number; errors: string[] }> =
      {};

    Object.entries(validationErrors).forEach(([milestoneId, errors]) => {
      const milestone = milestones?.find((m) => m?.id === milestoneId);
      const milestoneName = milestone?.title || `المعاملة (${milestoneId})`;
      // Find the milestone's index in the original array
      const milestoneIndex = milestones.findIndex((m) => m?.id === milestoneId);

      if (!groupedErrors[milestoneName]) {
        groupedErrors[milestoneName] = {
          index: milestoneIndex !== -1 ? milestoneIndex : 0,
          errors: [],
        };
      }

      errors.forEach((err: any) => {
        const vars = extractErrorVariables(err?.errorCode, err?.message);
        const msg = err?.errorCode
          ? t(PUBLISH_ERROR_CODES[err?.errorCode], {
              ...(err?.errorCode === "CAS.E0003"
                ? { val1: vars?.[0], val2: vars?.[1] }
                : {}),
            })
          : err?.localizedMessage || err?.message || "حدث خطأ";
        groupedErrors[milestoneName].errors.push(msg);
      });
    });

    // Format the grouped errors for display in the UI
    return groupedErrors;
  }

  const handlePublishAll = async () => {
    const draftMilestonesLeftToPublish = milestones.filter(
      (milestone) => milestone?.status === ECONTRACT_STATUS.DRAFT
    );
    try {
      setLoading(true);

      // Publish the complex contract if it is draft
      if (
        complexId &&
        milestones?.length !== 0 &&
        contract?.status === ECONTRACT_STATUS.DRAFT
      ) {
        await publishExternalDraftContract(complexId);
      }
      // We should publish the draft milestones left if the complex was already published before
      else if (complexId && draftMilestonesLeftToPublish?.length !== 0) {
        for (const milestone of draftMilestonesLeftToPublish) {
          await publishExternalDraftContract(milestone?.id || "");
        }
      }

      // Get names and indices of published milestones
      const successfulMilestones = draftMilestonesLeftToPublish
        .map((milestone) => {
          const milestoneIndex = milestones.findIndex(
            (m) => m?.id === milestone?.id
          );
          return {
            name: milestone?.title || "",
            index: milestoneIndex !== -1 ? milestoneIndex : 0,
          };
        })
        .filter((item) => item.name); // Filter out empty names

      // Display success modal
      setPublishSuccess(true);
      setPublishedMilestones(successfulMilestones);
      setPublishResultModalOpen(true);

      // Don't reload immediately to show the modal first
      // We'll reload after the user closes the modal
    } catch (error: any) {
      console.error(error);
      // Parse the validation errors
      const groupedErrors = formatMilestoneValidationErrors(
        error?.error?.validationErrors
      );

      // Display error modal
      setPublishSuccess(false);
      setPublishErrors(groupedErrors);
      setPublishResultModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle closing the publish result modal
  const handleClosePublishResultModal = () => {
    setPublishResultModalOpen(false);
    // If publication was successful, reload the page
    if (publishSuccess) {
      window?.location?.reload();
    }
  };

  return (
    <div
      className={classNames(styles.contractContainer, {
        [styles.paddedContainer]: !isCreateMatch,
      })}>
      {/* // TODO: depend on URL instead */}
      {showBreadCrumbs && (
        <div
          style={{
            width: "100%",
            paddingBottom: "20px",
            borderBottom: "1px solid #EEF0F5",
            position: "fixed",
            paddingTop: "32px",
            background: "white",
          }}>
          <BreadCrumbs
            activeIndex={1}
            separator={<LeftArrow />}
            items={[
              {
                label: location?.state?.from?.includes(ROUTES.CONTRACTS_LIST)
                  ? t("lbl_projects")
                  : t("lbl_business_home"),
                onClick: () =>
                  navigate(
                    location?.state?.from?.includes(ROUTES.CONTRACTS_LIST)
                      ? ROUTES.CONTRACTS_LIST
                      : ROUTES.HOME
                  ),
                visited: true,
              },
              {
                label: title,
              },
            ]}
          />
        </div>
      )}
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          paddingTop: showBreadCrumbs ? "100px" : "",
        }}>
        <div className={styles.headerBlock}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.subtitle}>{description}</p>
        </div>
        {(contract?.status === ECONTRACT_STATUS.DRAFT ||
          milestones?.some(
            (milestone) => milestone?.status === ECONTRACT_STATUS.DRAFT
          )) && (
          <PrimaryButton
            onClick={handlePublishAll}
            style={{
              flex: 1,
              width: "229px",
              height: "44px",
              borderRadius: "10px",
              textAlign: "center",
              whiteSpace: "nowrap",
              fontSize: "14px",
              lineHeight: "100%",
              fontWeight: 600,
              backgroundColor: "#0C4BC4",
            }}>
            {loading ? <Spinner /> : t("lbl_publish_all_milestones")}
          </PrimaryButton>
        )}
      </div>
      <div className={styles.statsRow}>
        {Object.keys(complexStats)?.map((statKey, index) => (
          <div key={statKey} className={styles.statCard}>
            <p className={styles.statLabel}>
              {index === 0
                ? t("lbl_total_project_value")
                : index === 1
                ? t("lbl_total_parties")
                : t("lbl_total_financial_milestones")}
            </p>
            <h3
              className={styles.statValue}
              style={{
                color:
                  index === 0 ? "#0D44AD" : index === 1 ? "#10131D" : "#00897B",
              }}>
              {`${complexStats?.[statKey]} ${
                index === 0 ? t("lbl_saudi_currency") : ""
              }`}
            </h3>
          </div>
        ))}
      </div>
      <div className={styles.milestonesBlock}>
        <div className={styles.buttonGroup}>
          <h2>{t("lbl_transactions")}</h2>
          {handleAddMilestone &&
            (authorities?.includes("CLIENT_ADMIN_CREATE_CONTRACT") ||
              authorities?.includes("CLIENT_ADMIN") ||
              authorities?.includes("ORGANIZATION_OWNER")) && (
              <p onClick={handleAddMilestone} className={styles.editLink}>
                {t("lbl_add_financial_milestone")}
              </p>
            )}
        </div>
        <div className={styles.milestonesBlock}>
          {milestones?.map((milestone, index) => {
            const currentCustomersAmount =
              milestone?.parties
                ?.filter(
                  (party) =>
                    party?.type === "customer" ||
                    (party as { role: ESenderRole })?.role ===
                      ESenderRole.CUSTOMER
                )
                ?.reduce((acc, cur) => {
                  return +acc + +(cur?.amount || 0);
                }, 0) ?? 0;
            const currentProvidersAmount =
              milestone?.parties
                ?.filter(
                  (party) =>
                    party?.type === "provider" ||
                    (party as { role: ESenderRole })?.role ===
                      ESenderRole.PROVIDER
                )
                ?.reduce((acc, cur) => {
                  return +acc + +(cur?.amount || 0);
                }, 0) ?? 0;
            const formattedDeadline = milestone?.deadline
              ? new Date(milestone.deadline).toLocaleDateString("ar-EG", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
              : "";

            const milestoneAmount = Math.max(
              currentCustomersAmount,
              currentProvidersAmount
            );
            // A milestone's cash-in transaction (type CUSTOMER_PAY) came in via
            // CONTRACT_TRANSFER rather than a direct buyer payment (card/bank/etc)
            // only when it's funded by another linked contract — which, for a
            // milestone inside the buyer's own source contract, means it's a
            // back-to-source refund from the destination contract.
            const cashInTransaction = milestone?.transactions?.find(
              (transaction) =>
                transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                transaction?.status === "SUCCESS"
            );
            const isBackToSourceRefund =
              index > 0 &&
              cashInTransaction?.paymentMethod ===
                EPaymentBrand.CONTRACT_TRANSFER;
            const formattedRefundDate = cashInTransaction?.createdAt
              ? new Date(cashInTransaction.createdAt).toLocaleDateString(
                  "ar-EG",
                  { day: "numeric", month: "long", year: "numeric" }
                )
              : formattedDeadline;

            const milestoneHref = !isCreateMatch
              ? milestone.status !== ECONTRACT_STATUS.DRAFT
                ? ROUTES.CONTRACT_DETAILS?.replace(":id", milestone?.id || "")
                : authorities?.includes("CLIENT_ADMIN_UPDATE_CONTRACT") ||
                  authorities?.includes("CLIENT_ADMIN") ||
                  authorities?.includes("ORGANIZATION_OWNER")
                ? ROUTES.CREATE_COMPLEX_CONTRACT?.replace(
                    ":milestoneId?",
                    milestone?.id || ""
                  ).replace(":complexId?", milestone?.parentId || "")
                : undefined
              : undefined;

            return (
              <a
                key={milestone?.title}
                className={classNames(styles.milestoneRow, {
                  [styles.refundRow]: isBackToSourceRefund,
                })}
                href={milestoneHref}
                onClick={(e) => {
                  e.preventDefault();
                  if (!isCreateMatch) {
                    if (milestone.status !== ECONTRACT_STATUS.DRAFT) {
                      navigate(
                        ROUTES.CONTRACT_DETAILS?.replace(
                          ":id",
                          milestone?.id || ""
                        ),
                        { state: { from: location?.state?.from } }
                      );
                    } else if (
                      authorities?.includes("CLIENT_ADMIN_UPDATE_CONTRACT") ||
                      authorities?.includes("CLIENT_ADMIN") ||
                      authorities?.includes("ORGANIZATION_OWNER")
                    ) {
                      navigate(
                        ROUTES.CREATE_COMPLEX_CONTRACT?.replace(
                          ":milestoneId?",
                          milestone?.id || ""
                        ).replace(":complexId?", milestone?.parentId || ""),
                        { state: { from: location?.state?.from } }
                      );
                    }
                    actions.resetContractState();
                  }
                }}
                style={{
                  cursor: !isCreateMatch ? "pointer" : "default",
                }}>
                <img src="/assets/images/svg/flag.svg" alt="flag" />
                <div className={styles.milestoneCol}>
                  <h4 className={styles.milestoneTitle}>
                    {isBackToSourceRefund
                      ? `${t("lbl_refund_from")} : ${milestones?.[0]?.title}`
                      : `${t("lbl_milestone")} ${getArabicOrdinalFemale(
                          index + 1
                        )} : ${milestone?.title}`}
                  </h4>
                  <p className={styles.milestoneMeta}>{`${t(
                    isBackToSourceRefund ? "lbl_refund_date" : "lbl_deadline"
                  )} : ${
                    isBackToSourceRefund
                      ? formattedRefundDate
                      : formattedDeadline
                  }`}</p>
                  <div className={styles.milestoneStats}>
                    <div>
                      <p className={styles.metaLabel}>
                        {t(
                          isBackToSourceRefund
                            ? "lbl_refund_value"
                            : "lbl_milestone_value"
                        )}
                      </p>
                      <h5 className={styles.metaValue}>
                        {`${milestoneAmount?.toLocaleString()} ${t(
                          "lbl_saudi_currency"
                        )}`}
                      </h5>
                    </div>
                  </div>
                </div>
                <div className={styles.milestoneSideCol}>
                  <div
                    className={classNames(styles.statusPill, {
                      [styles.statusActive]:
                        !isBackToSourceRefund &&
                        milestone?.status &&
                        milestone?.status !== ECONTRACT_STATUS.DRAFT,
                      [styles.refundBadge]: isBackToSourceRefund,
                    })}>
                    <p>
                      {isBackToSourceRefund
                        ? t("lbl_refunded_amount")
                        : milestone?.status
                        ? seller[milestone?.status]?.title
                        : t("lbl_draft")}
                    </p>
                  </div>
                  <div className={styles.milestoneActions}>
                    {handleEditMilestone &&
                      milestone?.status !== ECONTRACT_STATUS.CREATED && (
                        <p
                          onClick={(e) => {
                            e?.stopPropagation();

                            handleEditMilestone(index);
                          }}
                          className={styles.editLink}>
                          {t("lbl_edit")}
                        </p>
                      )}
                    {handleDeleteMilestone &&
                      (milestone?.status === ECONTRACT_STATUS.DRAFT ||
                        milestone?.status === ECONTRACT_STATUS.CREATED) && (
                        <p
                          onClick={(e) => {
                            e?.stopPropagation();
                            setModalOpen(true);
                            setSelectedIndex(index);
                          }}
                          className={classNames(styles.deleteLink, {
                            [styles.disabledLink]: milestones?.length <= 1,
                          })}>
                          {t("lbl_delete")}
                        </p>
                      )}
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
      {!isCreateMatch && complexId && (
        <SettlementRequests complexContractId={complexId} />
      )}

      <ConfirmationModal
        handleClose={() => {
          setModalOpen(false);

          setSelectedIndex(undefined);
        }}
        handleConfirm={() => {
          setModalOpen(false);
          handleDeleteMilestone?.(selectedIndex || 0);
        }}
        modalOpen={modalOpen}
        title={t("lbl_confirm_delete")}
        description={`${t("lbl_transaction_will_be_deleted")} : ${
          milestones?.[selectedIndex || 0]?.title
        }`}
        horizontalButtons
        icon={<DeleteResource />}
        confirmText={t("remove")}
        cancelText={t("lbl_delete_transaction_back_btn")}
        containerStyle={styles.modalContainer}
      />

      {/* Publish result modal */}
      <PublishResultModal
        isOpen={publishResultModalOpen}
        onClose={handleClosePublishResultModal}
        isSuccess={publishSuccess}
        milestones={publishedMilestones}
        errors={publishErrors}
      />
    </div>
  );
};

export default ComplexContractDetails;
